import type { CSSProperties } from 'react';
import type { ObjectPosition, SlideBg } from './types';

export const BG_SCALE_MIN = 1;
export const BG_SCALE_MAX = 2;
export const BG_SCALE_STEP = 0.05;

/** Preset anchor → focal point (percent). */
export function focalFromPosition(position?: ObjectPosition): { x: number; y: number } {
  switch (position) {
    case 'left':
      return { x: 0, y: 50 };
    case 'right':
      return { x: 100, y: 50 };
    case 'top':
      return { x: 50, y: 0 };
    case 'bottom':
    case 'object-bottom':
      return { x: 50, y: 100 };
    case 'bottom-center':
      return { x: 50, y: 85 };
    case 'center':
    default:
      return { x: 50, y: 50 };
  }
}

/** Resolved focal point for rendering and controls. */
export function getBgFocal(bg?: SlideBg): { x: number; y: number } {
  if (bg?.focalX != null && bg?.focalY != null) {
    return {
      x: clampPct(bg.focalX),
      y: clampPct(bg.focalY),
    };
  }
  return focalFromPosition(bg?.position);
}

export function clampPct(n: number): number {
  return Math.max(0, Math.min(100, n));
}

export function clampBgScale(n: number): number {
  return Math.max(BG_SCALE_MIN, Math.min(BG_SCALE_MAX, +n.toFixed(2)));
}

export function clampBgOpacity(n: number): number {
  return Math.max(0, Math.min(1, +n.toFixed(2)));
}

/** Resolved opacity for rendering (default 1). */
export function getBgOpacity(bg?: SlideBg): number {
  return bg?.opacity != null ? clampBgOpacity(bg.opacity) : 1;
}

export function patchBgOpacity(bg: SlideBg, opacity: number): SlideBg {
  const o = clampBgOpacity(opacity);
  if (o >= 1) {
    if (bg.opacity == null) return bg;
    const next = { ...bg };
    delete next.opacity;
    return next;
  }
  return { ...bg, opacity: o };
}

export function patchBgFocal(bg: SlideBg, x: number, y: number): SlideBg {
  return {
    ...bg,
    focalX: clampPct(x),
    focalY: clampPct(y),
  };
}

export function patchBgScale(bg: SlideBg, scale: number): SlideBg {
  const s = clampBgScale(scale);
  return s === 1 ? omitScale(bg) : { ...bg, scale: s };
}

function omitScale(bg: SlideBg): SlideBg {
  if (bg.scale == null) return bg;
  const next = { ...bg };
  delete next.scale;
  return next;
}

/** Inline styles for a full-bleed background image (matches SlideFrame). */
export function slideBgImageStyle(bg?: SlideBg): CSSProperties {
  if (!bg?.src) return {};
  const focal = getBgFocal(bg);
  const scale = bg.scale && bg.scale !== 1 ? bg.scale : 1;
  const transforms: string[] = [];
  if (bg.flipX) transforms.push('scaleX(-1)');
  if (scale !== 1) transforms.push(`scale(${scale})`);

  const opacity = getBgOpacity(bg);

  return {
    objectPosition: `${focal.x}% ${focal.y}%`,
    transform: transforms.length > 0 ? transforms.join(' ') : undefined,
    transformOrigin: `${focal.x}% ${focal.y}%`,
    opacity: opacity < 1 ? opacity : undefined,
  };
}

export const BG_POSITION_PRESETS: { id: ObjectPosition; label: string }[] = [
  { id: 'center', label: 'Center' },
  { id: 'left', label: 'Left' },
  { id: 'right', label: 'Right' },
  { id: 'top', label: 'Top' },
  { id: 'bottom', label: 'Bottom' },
];

export function applyBgPositionPreset(bg: SlideBg, position: ObjectPosition): SlideBg {
  const focal = focalFromPosition(position);
  return {
    ...bg,
    position,
    focalX: focal.x,
    focalY: focal.y,
  };
}

export function flipBgFocalX(bg?: SlideBg): SlideBg | undefined {
  if (!bg) return bg;
  const focal = getBgFocal(bg);
  return {
    ...bg,
    position: flipObjectPosition(bg.position),
    focalX: 100 - focal.x,
    focalY: focal.y,
  };
}

function flipObjectPosition(p?: ObjectPosition): ObjectPosition | undefined {
  if (p === 'left') return 'right';
  if (p === 'right') return 'left';
  return p;
}
